/**
 * Login Page Component
 *
 * Provides user authentication via email/password credentials.
 * Features a clean light theme consistent with the Armenian Link brand,
 * including animated elements, form validation, and error handling.
 *
 * @module pages/login
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Loader2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { signIn } from '@/client/lib/auth-client';
import { loginSchema, type LoginInput } from '@/client/lib/validations';
import { cn } from '@/client/lib/utils';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';

export function LoginPage() {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestAccess = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('request-access');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    try {
      const result = await signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        const errorMsg = result.error.message || 'Invalid email or password';
        toast.error(errorMsg);
        return;
      }

      toast.success('Welcome back, jan!');
      navigate('/links');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`An error occurred: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-xl group">
            <img
              src="/assets/logo.png"
              alt="Armenian Link"
              className="h-8 w-8 rounded-lg group-hover:scale-110 transition-transform"
            />
            <span className="text-foreground">Armenian Link</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to home
          </Link>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.5 }}
          className="w-full max-w-md relative"
        >
          {/* Card glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 rounded-2xl blur-lg opacity-60 -z-10" />

          <div className="relative bg-card rounded-2xl p-8 border border-border shadow-lg">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Barev! Welcome back</h1>
              <p className="text-muted-foreground">Sign in to your account to continue</p>
            </div>

            {/* Form */}
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  {...form.register('email')}
                  className={cn(
                    'w-full h-12 px-4 bg-background border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all',
                    form.formState.errors.email
                      ? 'border-destructive/50 focus:ring-destructive/50'
                      : 'border-border focus:ring-primary/50 focus:border-primary/50'
                  )}
                  placeholder="you@example.com"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive mt-2">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    spellCheck={false}
                    {...form.register('password')}
                    className={cn(
                      'w-full h-12 px-4 pr-12 bg-background border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all',
                      form.formState.errors.password
                        ? 'border-destructive/50 focus:ring-destructive/50'
                        : 'border-border focus:ring-primary/50 focus:border-primary/50'
                    )}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive mt-2">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: prefersReducedMotion ? 1 : 1.01 }}
                whileTap={{ scale: prefersReducedMotion ? 1 : 0.99 }}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-muted disabled:to-muted disabled:cursor-not-allowed text-zinc-900 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </motion.button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-border text-center">
              <p className="text-muted-foreground text-sm">
                Don&apos;t have an account?{' '}
                <button
                  onClick={handleRequestAccess}
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Request access
                </button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border py-6 bg-background">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Armenian Link. Made with pride.
          </p>
        </div>
      </footer>
    </div>
  );
}
